// src/pages/servicos/ordensServico/OrdemServicoConsultaPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaFileInvoice, FaFilter, FaPrint, FaSortDown, FaSortUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/input/Input';
import Select from '../../../components/input/Select';
import { useConfigModulo } from '../../../contexts/administrativo/ConfigModuloContext';
import { useFilial } from '../../../contexts/administrativo/FilialContext';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { usePrinterOS } from '../../../contexts/geral/PrinterOSContext';
import { useUser } from '../../../contexts/geral/UserContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import { useCliente } from '../../../contexts/pessoas/ClienteContext';
import { useConfigWhats } from '../../../contexts/pedidos/ConfigWhatsContext';
import { useOrdemServico } from '../../../contexts/servicos/OrdemServicoContext';
import {
  dataHora,
  FATURAMENTOS_OS,
  moeda,
  PAGAMENTOS_OS,
  PRIORIDADES_OS,
  SITUACOES_OS,
} from '../../../models/servicos/OrdemServico';
import {
  CAMPOS_FILTRO_AVANCADO_OS,
  OrdemServicoFiltroAvancado,
} from '../../../models/servicos/OrdemServicoFiltroAvancado';
import { Nota } from '../../../models/vendas/Nota';
import { NotaItem } from '../../../models/vendas/NotaItem';
import ModalFinalizarVenda from '../../vendas/ModalFinalizarVenda';
import OrdemServicoFiltroAvancadoModal from './OrdemServicoFiltroAvancadoModal';
import './Servicos.css';

const formatarDataInput = data => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const OrdemServicoConsultaPage = () => {
  const api = useOrdemServico();
  const { filialLogin } = useFilial();
  const { configModuloSel } = useConfigModulo();
  const usaPrioridadeOS = configModuloSel?.usaPrioridadeOS === true;
  const { imprimirOrdemServicoPDF } = usePrinterOS();
  const { user } = useUser();
  const { showYesNo } = useYesNo();
  const { showMessage } = useMessage();
  const { configWhats } = useConfigWhats();
  const { clientes, showClienteSearch } = useCliente();
  const navigate = useNavigate();
  const filialBuscaAutomaticaRef = useRef(null);
  const [filtros, setFiltros] = useState(() => {
    const hoje = new Date();
    return {
      numero: '',
      clienteId: '',
      clienteNome: '',
      situacao: '',
      prioridade: '',
      dataInicial: formatarDataInput(new Date(hoje.getFullYear(), hoje.getMonth() - 6, 1)),
      dataFinal: formatarDataInput(hoje),
      emAtraso: false,
      aguardandoFaturamento: false,
    };
  });
  const [filtrosAvancados, setFiltrosAvancados] = useState(() => new OrdemServicoFiltroAvancado());
  const [showFiltroAvancado, setShowFiltroAvancado] = useState(false);
  const camposAvancadosAtivos = CAMPOS_FILTRO_AVANCADO_OS.filter(({ campo }) => filtrosAvancados[campo]);
  const [ordensSelecionadas, setOrdensSelecionadas] = useState([]);
  const [nota, setNota] = useState(new Nota());
  const [showFinalizarVenda, setShowFinalizarVenda] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });

  const alterarFiltro = (campo, valor) => {
    setFiltros(anteriores => ({
      ...anteriores,
      [campo]: valor,
    }));
  };

  const buscar = (avancados = filtrosAvancados) => {
    setOrdensSelecionadas([]);
    return api.listar({
      filialId: filialLogin?.id,
      numero: filtros.numero || undefined,
      clienteId: filtros.clienteId || undefined,
      situacao: filtros.situacao || undefined,
      prioridade: usaPrioridadeOS ? filtros.prioridade || undefined : undefined,
      dataInicial: filtros.dataInicial || undefined,
      dataFinal: filtros.dataFinal || undefined,
      emAtraso: filtros.emAtraso || undefined,
      faturamento: filtros.aguardandoFaturamento ? 1 : undefined,
      ...avancados,
    });
  };

  const aplicarFiltroAvancado = novosFiltros => {
    setFiltrosAvancados(novosFiltros);
    setShowFiltroAvancado(false);
    return buscar(novosFiltros);
  };

  const selecionarCliente = cliente => {
    alterarFiltro('clienteId', cliente?.id || '');
    alterarFiltro(
      'clienteNome',
      cliente?.pessoa?.nome || cliente?.nome || '',
    );
  };

  const handleClienteExit = clienteId => {
    if (!clienteId) {
      selecionarCliente(null);
      return;
    }

    const cliente = clientes.find(item => Number(item.id) === Number(clienteId));
    if (cliente) {
      selecionarCliente(cliente);
      return;
    }

    showMessage('Cliente não encontrado!', 'warning');
    selecionarCliente(null);
  };

  const buscarCliente = async () => {
    const cliente = await showClienteSearch();
    if (cliente?.id) selecionarCliente(cliente);
  };

  const ordemElegivel = ordem => (
    [8, 9].includes(Number(ordem.situacao))
    && Number(ordem.situacaoFaturamento) === 1
  );

  const clienteSelecionadoId = api.ordens.find(
    ordem => ordensSelecionadas.includes(ordem.id),
  )?.clienteId;

  const alternarOrdem = ordem => {
    if (!ordemElegivel(ordem)) return;
    if (clienteSelecionadoId && Number(clienteSelecionadoId) !== Number(ordem.clienteId)) return;

    setOrdensSelecionadas(anteriores => (
      anteriores.includes(ordem.id)
        ? anteriores.filter(id => id !== ordem.id)
        : [...anteriores, ordem.id]
    ));
  };

  const montarNota = previa => Object.assign(new Nota(), {
    filialId: previa.filialId,
    clienteId: previa.clienteId,
    clienteNome: previa.clienteNome,
    naturezaId: 1,
    caixaId: 1,
    tipoMov: 2,
    ordensServicoIds: previa.ordensServicoIds,
    vProd: Number(previa.valorProdutos || 0) + Number(previa.valorServicos || 0),
    vDesc: Number(previa.valorDesconto || 0),
    vNota: Number(previa.valorTotal || 0),
    observacao: `Faturamento das OS nº ${previa.numerosOrdensServico
      .map(numero => String(numero).padStart(6, '0')).join(', ')}.`,
    notaItens: previa.itens.map((item, indice) => Object.assign(new NotaItem(), {
      produtoId: item.produtoId,
      ordemServicoItemId: item.ordemServicoItemId,
      item: indice + 1,
      xProd: item.xProd,
      qtdCom: Number(item.quantidade || 0),
      vUnCom: Number(item.quantidade || 0) > 0
        ? Number(item.valorTotal || 0) / Number(item.quantidade)
        : Number(item.valorUnitario || 0),
      vProd: Number(item.valorTotal || 0),
      produto: {
        id: item.produtoId,
        descricao: item.xProd,
        ehServico: Number(item.tipo) === 2,
      },
    })),
  });

  const abrirFaturamento = async () => {
    if (ordensSelecionadas.length === 0) return;
    const previa = await api.prepararFaturamento(ordensSelecionadas);
    if (!previa) return;
    setNota(montarNota(previa));
    setShowFinalizarVenda(true);
  };

  const fecharFaturamento = () => {
    setShowFinalizarVenda(false);
    setNota(new Nota());
  };

  const ordensOrdenadas = useMemo(() => {
    const { campo, direcao } = ordenacao;
    if (!campo) return api.ordens;

    const multiplicador = direcao === 'asc' ? 1 : -1;
    return [...api.ordens].sort((a, b) => {
      const obterValor = ordem => {
        if (campo === 'situacaoDescricao') return SITUACOES_OS[ordem.situacao];
        if (campo === 'faturamentoDescricao') return FATURAMENTOS_OS[ordem.situacaoFaturamento];
        if (campo === 'pagamentoDescricao') return PAGAMENTOS_OS[ordem.situacaoPagamento];
        if (campo === 'prioridadeDescricao') return PRIORIDADES_OS[ordem.prioridade];
        return ordem?.[campo];
      };
      const valorA = obterValor(a);
      const valorB = obterValor(b);

      if (['numero', 'valorTotal'].includes(campo)) {
        return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
      }
      if (campo === 'dataAbertura') {
        return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
      }
      return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
        sensitivity: 'base',
      }) * multiplicador;
    });
  }, [api.ordens, ordenacao]);

  const alterarOrdenacao = campo => {
    setOrdenacao(atual => ({
      campo,
      direcao: atual.campo === campo && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderOrdenadorColuna = (campo, titulo, style = {}) => {
    const ativa = ordenacao.campo === campo;
    return (
      <button
        type="button"
        onClick={() => alterarOrdenacao(campo)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          fontWeight: 'bold',
          color: 'inherit',
          cursor: 'pointer',
          width: '100%',
          textAlign: style.textAlign || 'left',
        }}
      >
        {titulo}
        {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
      </button>
    );
  };

  const confirmarFaturamento = async (_nota, options = {}) => {
    if (!options.notaEmitida) return;
    const idsFaturadas = [...ordensSelecionadas];
    const resultado = await api.faturarLote({
      usuarioId: user?.id,
      ordensServicoIds: idsFaturadas,
    });
    if (!resultado) return;
    fecharFaturamento();
    setOrdensSelecionadas([]);
    await buscar();

    if (!configWhats?.desativarWhats
      && options.documentoFiscalEmitido
      && resultado.some(ordemFaturada => ordemFaturada.clientePossuiTelefone)) {
      const deveEnviar = await showYesNo(
        'Deseja enviar os documentos fiscais para o cliente pelo WhatsApp?',
        'no',
      );
      if (deveEnviar) {
        await api.enviarDocumentosWhatsApp(idsFaturadas);
      }
    }
  };

  useEffect(() => {
    if (filialLogin?.id && filialBuscaAutomaticaRef.current !== filialLogin.id) {
      filialBuscaAutomaticaRef.current = filialLogin.id;
      buscar();
    }
  }, [filialLogin?.id]);

  return (
    <div className="servicos-page container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h3>Ordens de serviço</h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/ordemServico')}
        >
          Nova OS
        </button>
      </div>

      <section className="servicos-card os-consulta-filtros-card">
        <div className="os-consulta-filtros">
          <div className="os-filtro-numero">
            <Input
              label="Número"
              value={filtros.numero}
              size={100}
              onChange={evento => alterarFiltro('numero', evento.target.value)}
            />
          </div>
          <div className="os-filtro-cliente d-flex gap-2 align-items-end">
            <Input
              label="Cliente"
              value={filtros.clienteId}
              size={100}
              onChange={evento => alterarFiltro(
                'clienteId',
                Number(evento.target.value) || '',
              )}
              onBlur={evento => handleClienteExit(evento.target.value)}
              btnBuscar={buscarCliente}
            />
            <Input
              label="Nome do cliente"
              value={filtros.clienteNome}
              size={280}
              disabled
            />
          </div>
          <div className="os-filtro-select">
            <Select
              label="Situação"
              size={200}
              value={filtros.situacao}
              onChange={evento => alterarFiltro('situacao', evento.target.value)}
              options={[
                { value: '', label: 'Todas' },
                ...Object.entries(SITUACOES_OS).map(([value, label]) => ({ value, label })),
              ]}
            />
          </div>
          {usaPrioridadeOS && (
            <div className="os-filtro-prioridade">
              <Select
                label="Prioridade"
                size={90}
                value={filtros.prioridade}
                onChange={evento => alterarFiltro('prioridade', evento.target.value)}
                options={[
                  { value: '', label: 'Todas' },
                  ...Object.entries(PRIORIDADES_OS).map(([value, label]) => ({ value, label })),
                ]}
              />
            </div>
          )}
          <Input
            label="Data inicial"
            type="date"
            value={filtros.dataInicial}
            size={130}
            onChange={evento => alterarFiltro('dataInicial', evento.target.value)}
          />
          <Input
            label="Data final"
            type="date"
            value={filtros.dataFinal}
            size={130}
            onChange={evento => alterarFiltro('dataFinal', evento.target.value)}
          />
          {/*
          <div className="form-check mb-2">
            <input
              id="os-atraso"
              className="form-check-input"
              type="checkbox"
              checked={filtros.emAtraso}
              onChange={evento => alterarFiltro('emAtraso', evento.target.checked)}
            />
            <label htmlFor="os-atraso">Somente atrasadas</label>
          </div>
          */}
          <div className="form-check mb-2">
            <input
              id="os-aguardando-faturamento"
              className="form-check-input"
              type="checkbox"
              checked={filtros.aguardandoFaturamento}
              onChange={evento => alterarFiltro(
                'aguardandoFaturamento',
                evento.target.checked,
              )}
            />
            <label htmlFor="os-aguardando-faturamento">Aguardando faturamento</label>
          </div>
          <div className="os-filtro-buscar d-flex flex-wrap gap-2 mb-1">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={api.loading}
              onClick={() => setShowFiltroAvancado(true)}
            >
              <FaFilter aria-hidden="true" /> Filtro avançado
              {camposAvancadosAtivos.length > 0 && ` (${camposAvancadosAtivos.length})`}
            </button>
            <button
              type="button"
              className="btn btn-search"
              disabled={api.loading}
              onClick={() => buscar()}
            >
              {api.loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
        {camposAvancadosAtivos.length > 0 && (
          <div className="os-filtros-avancados-ativos alert alert-info mt-3 mb-0" role="status">
            <strong>Filtros avançados ativos ({camposAvancadosAtivos.length})</strong>
            <ul className="mb-0 mt-1">
              {camposAvancadosAtivos.map(({ campo, label }) => (
                <li key={campo}><strong>{label}:</strong> {filtrosAvancados[campo]}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="servicos-card">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <span>
            {ordensSelecionadas.length} ordem(ns) selecionada(s) — total{' '}
            <strong>{moeda(api.ordens
              .filter(ordem => ordensSelecionadas.includes(ordem.id))
              .reduce((total, ordem) => total + Number(ordem.valorTotal || 0), 0))}</strong>
          </span>
          <button
            type="button"
            className="btn btn-success"
            disabled={api.loading || ordensSelecionadas.length === 0}
            onClick={abrirFaturamento}
          >
            Faturar selecionadas <FaFileInvoice />
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th aria-label="Selecionar" />
                <th>{renderOrdenadorColuna('numero', 'OS')}</th>
                <th>{renderOrdenadorColuna('dataAbertura', 'Abertura')}</th>
                <th>{renderOrdenadorColuna('clienteNome', 'Cliente')}</th>
                <th>{renderOrdenadorColuna('equipamentoDescricao', 'Equipamento')}</th>
                <th>{renderOrdenadorColuna('situacaoDescricao', 'Situação')}</th>
                <th>{renderOrdenadorColuna('faturamentoDescricao', 'Faturamento')}</th>
                <th>{renderOrdenadorColuna('pagamentoDescricao', 'Pagamento')}</th>
                {usaPrioridadeOS && <th>{renderOrdenadorColuna('prioridadeDescricao', 'Prioridade')}</th>}
                <th>{renderOrdenadorColuna('valorTotal', 'Total')}</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {ordensOrdenadas.map(ordemServico => (
                <tr
                  key={ordemServico.id}
                  className={ordemServico.emAtraso ? 'table-danger' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Selecionar OS ${ordemServico.numero}`}
                      checked={ordensSelecionadas.includes(ordemServico.id)}
                      disabled={
                        !ordemElegivel(ordemServico)
                        || (clienteSelecionadoId
                          && Number(clienteSelecionadoId) !== Number(ordemServico.clienteId))
                      }
                      onChange={() => alternarOrdem(ordemServico)}
                    />
                  </td>
                  <td>{String(ordemServico.numero).padStart(6, '0')}</td>
                  <td>{dataHora(ordemServico.dataAbertura)}</td>
                  <td>{ordemServico.clienteNome}</td>
                  <td>{ordemServico.equipamentoDescricao || '-'}</td>
                  <td>
                    <span className={`os-badge os-situacao-${ordemServico.situacao}`}>
                      {SITUACOES_OS[ordemServico.situacao]}
                    </span>
                  </td>
                  <td>{FATURAMENTOS_OS[ordemServico.situacaoFaturamento] || '-'}</td>
                  <td className={ordemServico.possuiParcelaVencida ? 'text-danger' : ''}>
                    {PAGAMENTOS_OS[ordemServico.situacaoPagamento] || '-'}
                    {ordemServico.possuiParcelaVencida && ' (vencido)'}
                  </td>
                  {usaPrioridadeOS && <td>{PRIORIDADES_OS[ordemServico.prioridade]}</td>}
                  <td>{moeda(ordemServico.valorTotal)}</td>
                  <td>
                    <div className="d-flex justify-content-center flex-wrap gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/ordemServico/${ordemServico.id}`)}
                      >
                        Abrir
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => imprimirOrdemServicoPDF(ordemServico.id, 75)}
                      >
                        Imprimir <FaPrint />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!api.loading && api.ordens.length === 0 && (
            <p className="text-center text-muted">Nenhuma ordem encontrada.</p>
          )}
        </div>
      </section>
      <OrdemServicoFiltroAvancadoModal
        show={showFiltroAvancado}
        filtros={filtrosAvancados}
        loading={api.loading}
        onHide={() => setShowFiltroAvancado(false)}
        onApply={aplicarFiltroAvancado}
      />
      <ModalFinalizarVenda
        show={showFinalizarVenda}
        onHide={fecharFaturamento}
        nota={nota}
        setNota={setNota}
        onConfirm={confirmarFaturamento}
      />
    </div>
  );
};

export default OrdemServicoConsultaPage;
